<template>
  <div class="tw:bg-white tw:py-[60px]" ref="containerRef">
    <ContainerLayout class="tw:flex tw:flex-col">
      <h1
        class="header tw:text-[28px] tw:leading-[35px] tw:md:text-[44px] tw:md:leading-[55px] tw:pb-6 tw:lg:pb-12 tw:mx-auto tw:text-center"
        v-html="$t('rc_product_page_highlights_header')"
      />
      <div class="tw:flex tw:flex-col tw:xl:flex-row tw:w-full tw:gap-x-12 tw:gap-y-12">
        <div class="tw:w-full tw:xl:w-2/3">
          <div class="video-container">
            <div
              data-testid="rate-connect-video-player"
              ref="playerContainerRef"
              class="tw:w-full tw:bg-gray-100"
            ></div>
          </div>
        </div>
        <div class="tw:w-full tw:xl:w-1/3">
          <ul class="tw:flex tw:flex-col tw:gap-y-[34px]">
            <li v-for="benefit in benefits" :key="benefit.id" class="tw:flex tw:flex-col tw:gap-y-2">
              <div class="tw:flex tw:items-center tw:gap-x-2">
                <FullCheckIcon height="24px" width="24px" class="tw:inline tw:stroke-gray-700" />
                <p class="tw:font-bold tw:text-2xl">{{ $t(benefit.title) }}</p>
              </div>
              <p class="tw:pl-8 tw:lg:pl-0">{{ $t(benefit.text) }}</p>
            </li>
          </ul>
        </div>
      </div>
    </ContainerLayout>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import Player from '@vimeo/player'
import FullCheckIcon from '@/components/icons/FullCheckIcon.svg?component'
import ContainerLayout from '@/layouts/ContainerLayout.vue'

import * as Sentry from '@sentry/vue'
import { useSessionValues } from '@/composables/useSessionValues'

const containerRef = ref(null)
const playerContainerRef = ref(null)
const shouldLoad = ref(false)
const isInView = ref(false)
const { language } = useSessionValues()
const location = computed(() => language.value?.split('-')?.[0] ?? 'en')

const videoIds = {
  en: '1127153173',
  de: '1127157696',
  it: '1129127798',
}

const videoHashes = {
  de: 'e19ef4db77',
  en: '1a7bd1efc5',
  it: 'd5e3a4d82c',
}

let player = null
let preloadObserver = null
let playbackObserver = null
let playPromise = null

const waitForPendingPlay = async () => {
  if (!playPromise) return
  try {
    await playPromise
  } catch {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'rateConnectVideo')
      Sentry.captureException('`player.play()` is already handled at the creation site.')
    })
  }
}

const currentVideoId = computed(() => {
  const availableLocations = Object.keys(videoIds)
  const locale = availableLocations.includes(location.value) ? location.value : 'en'
  return videoIds[locale]
})

const currentHash = computed(() => {
  const availableLocations = Object.keys(videoIds)
  const locale = availableLocations.includes(location.value) ? location.value : 'en'
  return videoHashes[locale]
})

const videoUrl = computed(() => `https://player.vimeo.com/video/${currentVideoId.value}?h=${currentHash.value}`)

const initPlayer = async () => {
  if (!playerContainerRef.value || !shouldLoad.value) return

  if (player) {
    await waitForPendingPlay()
    await player.destroy()
    player = null
  }

  player = new Player(playerContainerRef.value, {
    url: videoUrl.value,
    autoplay: false,
    muted: true,
    controls: true,
    responsive: true,
    autopause: false,
    badge: false,
  })

  if (isInView.value) {
    try {
      playPromise = player.play()
      await playPromise
    } catch (error) {
      if (error.name) {
        Sentry.withScope(scope => {
          scope.setTag('feature', 'rateConnectVideo')
          Sentry.captureException(error)
        })
      }
    } finally {
      playPromise = null
    }
  }
}

watch([currentVideoId, currentHash], async () => {
  if (shouldLoad.value) {
    await initPlayer()
  }
})

onMounted(async () => {
  await nextTick()
  if (containerRef.value) {
    preloadObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !player) {
            shouldLoad.value = true
            initPlayer()
          }
        })
      },
      {
        threshold: 0,
        rootMargin: '400px 0px',
      }
    )
    preloadObserver.observe(containerRef.value)

    // Playback observer: strict viewport check drives play/pause
    playbackObserver = new IntersectionObserver(
      entries => {
        entries.forEach(async entry => {
          isInView.value = entry.isIntersecting
          if (player) {
            if (entry.isIntersecting) {
              // Wait for any pending play before starting new one
              if (playPromise) return

              try {
                playPromise = player.play()
                await playPromise
              } catch (error) {
                if (error.name) {
                  Sentry.withScope(scope => {
                    scope.setTag('feature', 'rateConnectVideo')
                    Sentry.captureException(error)
                  })
                }
              } finally {
                playPromise = null
              }
            } else {
              // Wait for play to complete before pausing
              const activePlayer = player
              await waitForPendingPlay()
              if (isInView.value || player !== activePlayer) return

              try {
                await activePlayer.pause()
              } catch (error) {
                Sentry.withScope(scope => {
                  scope.setTag('feature', 'rateConnectVideo')
                  Sentry.captureException(error)
                })
              }
            }
          }
        })
      },
      {
        threshold: 0.5,
      }
    )
    playbackObserver.observe(containerRef.value)
  }
})

onBeforeUnmount(async () => {
  if (preloadObserver) {
    preloadObserver.disconnect()
  }
  if (playbackObserver) {
    playbackObserver.disconnect()
  }

  await waitForPendingPlay()

  if (player) {
    try {
      await player.pause()
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'rateConnectVideo')
        Sentry.captureException(error)
      })
    }
    await player.destroy()
    player = null
  }
})

const benefits = ref([
  {
    id: 0,
    title: 'rc_product_page_highlight_1_header',
    text: 'rc_product_page_highlight_1_text',
  },
  {
    id: 1,
    title: 'rc_product_page_highlight_2_header',
    text: 'rc_product_page_highlight_2_text',
  },
  {
    id: 2,
    title: 'rc_product_page_highlight_3_header',
    text: 'rc_product_page_highlight_3_text',
  },
  {
    id: 3,
    title: 'rc_product_page_highlight_4_header',
    text: 'rc_product_page_highlight_4_text',
  },
])
</script>

<style scoped>
.header :deep(b) {
  text-wrap: nowrap;
}
.video-container {
  position: relative;
  overflow: hidden;
}
</style>
